import Layout from "../../layout/Layout";
import ProviderBanner from "./ProviderBanner";
import OrthoCheck from "./Orthocheck";
import DoctorProfile from "./DoctorProfile";
import PatientReviewsSection from "./PatientReviewsSection";
import PracticeLocations from "./PracticeLocations";
import MedCheckBanner from "./MedCheckBanner";
import HealthInsights from "./HealthInsights";
import PersonalLife from "./PersonalLife";
import EducationProfile from "./EducationProfile";
import EducationProfileUpdated from "./EducationProfileUpdated";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import LoadingScreen from "../../../../../components/custom/LoadingScreen";

const ProviderProfile = () => {
  const { npiId, practiceLocationGroupId } = useParams();
  const [providerData, setProviderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isRecommended = queryParams.get("recommended") === "true";
    const exploreProviderClicked =
      sessionStorage.getItem("exploreProviderClicked") === "true";

    const fetchProviderDetails = async () => {
      try {
        setLoading(true);
        let response = null;
        if (practiceLocationGroupId) {
          response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/v1/public/enhanced/practice/profile/${npiId}/${practiceLocationGroupId}`
          );
        } else {
          response = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/v1/public/enhanced-profile/${npiId}`
          );
        }

        const apiKey = response.data.medcheck_config.api_key;
        localStorage.removeItem("schedulers_customer_config");
        if (!exploreProviderClicked && !isRecommended) {
          localStorage.setItem("provider_api_key", apiKey);
          localStorage.setItem("provider_enhanced", "true");
        }
        localStorage.setItem("provider_info", JSON.stringify(response.data));
        setProviderData(response.data);
      } catch (error) {
        console.error("Error fetching provider details:", error);

        if (error.response && error.response.status === 404) {
          setNotFound(true); // mark as not found
        } else {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (npiId) {
      fetchProviderDetails();
    }
  }, [npiId, practiceLocationGroupId, location.search]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[calc(100vh-200px)] my-20">
          <LoadingScreen color="primary" />
        </div>
      </Layout>
    );
  }

  if (notFound) {
    return (
      <Layout>
        <div className="w-full h-screen">
          <iframe
            src={`${process.env.PUBLIC_URL}/404.html`}
            title="404 Page"
            style={{
              width: "100%",
              height: "100vh",
              border: "none",
              overflow: "hidden",
            }}
            scrolling="no"
          />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-lg text-red-600">Error: {error}</div>
        </div>
      </Layout>
    );
  }

  if (!providerData) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-lg">No provider data found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="provider-profile">
        {/* Provider Banner - Pass basic info, rating, reviews */}
        <ProviderBanner
          name={providerData.name}
          jobTitle={providerData.job_title}
          medicalClinic={providerData.medical_clinic}
          rating={providerData.rating}
          reviewCount={providerData.review_count}
          yearsOfExperience={providerData.years_of_experience}
          profileImage={providerData.about_image.url}
          telephone={providerData.telephone}
          address={providerData.address}
          description={providerData.description}
          businessName={providerData.google_business_name}
          placeId={providerData.google_place_id}
          apikey={providerData.medcheck_config?.api_key}
          isPractice={providerData.is_practice}
          locationGroupId={providerData.location_group_id}
        />
        {/* OrthoCheck - Pass relevant specialty for symptom checker */}
        <OrthoCheck
          specialty={providerData.relevant_specialty}
          knowsAbout={providerData.knows_about}
          isPractice={providerData.is_practice || false}
        />
        {/* Doctor Profile - Pass biography and about image */}
        {providerData.biography && !providerData.is_practice && (
          <DoctorProfile
            biography={providerData.biography}
            aboutImage={providerData.biography.image}
            name={providerData.name}
          />
        )}
        {/* Patient Reviews Section - Pass rating and review data */}
        {(providerData.google_place_id ||
          providerData?.google_business_name) && (
          <PatientReviewsSection
            businessName={providerData.google_business_name}
            placeId={providerData.google_place_id}
          />
        )}
        {/* Practice Locations - Pass address information */}
        {providerData.address?.length > 0 && (
          <PracticeLocations
            gender={providerData.gender}
            addresses={providerData.address}
            name={providerData.name}
          />
        )}
        {/* MedCheck Banner */}
        <MedCheckBanner />
        {/* Health Insights - Specialty-specific educational content */}
        <HealthInsights specialty={providerData.relevant_specialty} />
        {/* Personal Life - Pass personal life content */}
        {providerData.personal_life && (
          <PersonalLife
            personalLife={providerData.personal_life}
            name={providerData.given_name}
          />
        )}
        {/* Education Profile - Conditional rendering based on others type */}
        {providerData.others &&
          (Array.isArray(providerData.others) &&
          providerData.others.length > 0 ? (
            <EducationProfileUpdated tabData={providerData.others} />
          ) : !Array.isArray(providerData.others) &&
            Object.keys(providerData.others).length > 0 ? (
            <EducationProfile tabData={providerData.others} />
          ) : null)}
      </section>
    </Layout>
  );
};

export default ProviderProfile;
